/**
 * Trajectoire Widget Controller (EPIC-047.4)
 *
 * Compact widget on the dashboard payant (§15.B). Fetches the trajectory
 * for the current year and hydrates three gauges :
 *  - annual : CA réel YTD vs **objectif annuel total** (`trajectory.totalTargetHt`).
 *    La jauge se remplit progressivement vers 100 % au fil de l'année.
 *  - ytd : CA réel YTD vs **objectif cumulé à date** (`comparison.expectedYtd`,
 *    Σ mois écoulés + prorata du mois courant). Indique si l'on est dans les
 *    clous de la trajectoire jusqu'à aujourd'hui (ratio ~100 % = parfaitement
 *    aligné, < 100 % = en retard, > 100 % = en avance).
 *  - monthly : CA réel du mois courant vs objectif du mois courant
 *    (`comparison.expectedMonth`, sans prorata côté objectif).
 * Each gauge displays "<caReel> € / <objectif> € (<ratio> %)" in
 * `nm-jauge__amount`, fills `nm-jauge__fill` and adopts `is-warning` when
 * the ratio is < 100 %. If the trajectory does not exist (404), the widget
 * stays hidden.
 *
 * Mounted on the `.nm-card--trajectoire` block inside the
 * `dashboard-payant-template`. Activated automatically when the
 * dashboard-router clones the template into the DOM.
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";

export default class TrajectoireWidgetController extends BaseController {
  static targets = [
    "year",
    "gaugeAmount", "gaugeFill",
    "ytdGaugeAmount", "ytdGaugeFill",
    "monthGaugeAmount", "monthGaugeFill",
  ];

  async connect() {
    this._year = new Date().getFullYear();
    await this.load();
  }

  async load() {
    try {
      const data = await api.get("/me/trajectory", { year: String(this._year) });
      this.hydrate(data);
      this.element.hidden = false;
    } catch (_error) {
      this.element.hidden = true;
    }
  }

  hydrate(payload) {
    const trajectory = payload.trajectory || payload;
    const comparison = payload.comparison || {};
    const year = trajectory.year || this._year;

    if (this.hasYearTarget) this.yearTarget.textContent = String(year);

    const caRealYtd = parseFloat(comparison.caRealYtd) || 0;
    const totalTargetHt = parseFloat(trajectory.totalTargetHt) || 0;
    this._fillGauge(
      this.hasGaugeAmountTarget ? this.gaugeAmountTarget : null,
      this.hasGaugeFillTarget ? this.gaugeFillTarget : null,
      caRealYtd,
      totalTargetHt,
    );

    const expectedYtd = parseFloat(comparison.expectedYtd) || 0;
    this._fillGauge(
      this.hasYtdGaugeAmountTarget ? this.ytdGaugeAmountTarget : null,
      this.hasYtdGaugeFillTarget ? this.ytdGaugeFillTarget : null,
      caRealYtd,
      expectedYtd,
    );

    const caRealMonth = parseFloat(comparison.caRealMonth) || 0;
    const expectedMonth = parseFloat(comparison.expectedMonth) || 0;
    this._fillGauge(
      this.hasMonthGaugeAmountTarget ? this.monthGaugeAmountTarget : null,
      this.hasMonthGaugeFillTarget ? this.monthGaugeFillTarget : null,
      caRealMonth,
      expectedMonth,
    );
  }

  _fillGauge(amountEl, fillEl, caReal, target) {
    const ratio = target > 0 ? (caReal / target) * 100 : 0;
    const clamped = Math.min(100, Math.max(0, ratio));
    const ratioLabel = target > 0 ? `${ratio.toFixed(0)} %` : "— %";

    if (amountEl) {
      amountEl.textContent = `${formatEurShort(caReal)} / ${formatEurShort(target)} (${ratioLabel})`;
    }
    if (fillEl) {
      fillEl.style.width = `${clamped.toFixed(0)}%`;
      fillEl.classList.toggle("is-warning", target > 0 && ratio < 100);
    }
  }
}

function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(value));
}

function formatEurShort(value) {
  return `${formatNumber(value)} €`;
}
